import initializeMailRibbonConfiguration from "../actions/initializeMailRibbonConfiguration";
import { setTabControlsInOverflow } from "../actions/setTabControlsInOverflow";
import { getCurrentStore } from "../store/store";
import { hasEnterpriseAccount } from "./hasEnterpriseAccount";
import { logUsage } from "owa-analytics";
import type { MailboxInfo } from "owa-client-types";
import { isGlobalFeatureEnabled } from "owa-feature-flags";
import { defaultConfig, fillMissingRibbonTabs, getDefaultConfig as getRibbonDefaultConfig, mergedConfig } from "owa-mail-ribbon-store-shared-types";
import type { MailRibbonConfigStore, RibbonConfiguration, } from "owa-mail-ribbon-store-shared-types";
import { ribbonValidityCheck } from "owa-mail-ribbon-versioning/lib/ribbonValidityCheck";
import { ribbonVersionHandler } from "owa-mail-ribbon-versioning/lib/ribbonVersionHandler";
import type { RibbonControlId } from "owa-ribbon-ids";
import { type MailRibbonGroupId, type MailRibbonControlId } from "owa-ribbon-ids/lib/mailRibbonId";
import { getAccountScopeUserSettings } from "owa-session-store";
import isWin32StylingEnabled from "owa-theme-win32-style-store/lib/selectors/isWin32StylingEnabled";
import { updateAccountScopeUserSettingsAndService } from "owa-userconfiguration/lib/utils/updateAccountScopeUserSettingsAndService";

/**
 * Loads the appropriate default config to be used. This shouldn't affect customizable ribbon tabs.
 */
function getDefaultConfig() {
    return getRibbonDefaultConfig(isWin32StylingEnabled(), mergedConfig.get());
}

/**
 * Load the appropriate mail ribbon configuration for this mailboxInfo
 * This function calls owa-session-store's @see {getAccountScopeUserSettings} to pull in the
 * user's persisted ribbon. If they have never persisted before, it'll be an empty string.
 */
export default async function loadMailRibbonConfigurationForAccount(mailboxInfo: MailboxInfo): Promise<void> {
    // If we've already loaded the config, we don't need to do it again
    if (getCurrentStore(mailboxInfo).hasLoaded) {
        return;
    }
    
    /**
     * We start with an initial initialization of Ribbon, disregarding any customizations.
     * This allows the Ribbon to load quickly, and then we can update it later with the user's customization,
     * once it has done all the validityChecking and customization checking.
     */
    initializeMailRibbonConfiguration(getDefaultConfig(), mailboxInfo);
    
    if (isGlobalFeatureEnabled("mon-ribbon-customization-hard-reset")) {
        hardResetRibbon(mailboxInfo);
        return;
    }
    
    if (isWin32StylingEnabled()) {
        // TODO: Implement customization for Win32 styling https://dev.azure.com/outlookweb/Outlook%20Web/_workitems/edit/456368/
        return;
    }
    
    const ribbonConfigJSON = getAccountScopeUserSettings(mailboxInfo)?.ViewStateConfiguration?.MailRibbonConfig;
    
    if (!ribbonConfigJSON) {
        // Default scenario of an uncustomized ribbon
        
        if (!hasEnterpriseAccount()) {
            initializeConsumerMailRibbonConfiguration(mailboxInfo);
        }
        
        // We already default initialize the user to defaultConfig, so just logUsage and return
        logUsage("RibbonCustomization_Default", mailboxInfo);
        return;
    }
    
    // User has a customized ribbon.
    const ribbonConfig = fillMissingRibbonTabs(JSON.parse(ribbonConfigJSON) as MailRibbonConfigStore, getDefaultConfig());
    
    logUsage("RibbonCustomization_Custom", mailboxInfo);
    
    // Fail safe for an erroneous state that some users are in. See ADO246765 for more details.
    if (!checkForSinglelineCustomizationGroup(ribbonConfig.singleline)) {
        logUsage("RibbonCustomization_FailSafeTriggered", mailboxInfo);
        hardResetRibbon(mailboxInfo);
        return;
    }
    
    // Check if the user's customized Ribbon config is on a higher version than the app's, which means
    // that the user is on an old version of the app, which is dangerous because this older version
    // may not be able to parse some of the RibbonIDs in the user's config, which may cause the app to crash.
    if (ribbonConfig.version > defaultConfig.version) {
        // Run the user's config through a validityChecker to prune out bad IDs.
        // In this scenario of user version > defaultConfig version, we never reset the user's config.
        // Instead, if there's something we can't find, we instead locally prune it from the user's ribbon.
        // This allows the user to still use the ribbon with the majority of their customizations, while also
        // pruning away any ID that this version of Outlook may not be able to render.
        if (ribbonValidityCheck(ribbonConfig.singleline, ribbonConfig.version, mailboxInfo, true /*isSingleLine*/, true /* shouldPrune */)) {
            logUsage("RibbonVersioning_PassedValidityCheck_OlderConfigVersion", mailboxInfo, [
                ribbonConfig.version,
                defaultConfig.version,
                "singleline",
            ]);
        }
        else {
            logUsage("RibbonVersioning_FailedValidityCheck_OlderConfigVersion", mailboxInfo, [
                ribbonConfig.version,
                defaultConfig.version,
                "singleline",
            ]);
        }
        // Run the same validityChecker on the user's multiline ribbon if MLR customization is enabled
        if (isGlobalFeatureEnabled("mon-ribbon-mlr-customization")) {
            if (ribbonValidityCheck(ribbonConfig.multiline, ribbonConfig.version, mailboxInfo, false /*isSingleLine*/, true /* shouldPrune */)) {
                logUsage("RibbonVersioning_PassedValidityCheck_OlderConfigVersion", mailboxInfo, [
                    ribbonConfig.version,
                    defaultConfig.version,
                    "multiline",
                ]);
            }
            else {
                logUsage("RibbonVersioning_FailedValidityCheck_OlderConfigVersion", mailboxInfo, [
                    ribbonConfig.version,
                    defaultConfig.version,
                    "multiline",
                ]);
            }
        }
        
        // Now, update the uncustomizable tabs of the user's configuration file to match this version's defaultConfig.
        updateUncustomizableTabs(ribbonConfig);
        initializeMailRibbonConfiguration(ribbonConfig, mailboxInfo);
    }
    else {
        updateUncustomizableTabs(ribbonConfig);
        
        // Now, check if the user's customized Ribbon config is on a lower version than the app's.
        // If so, this means we want to update the user's Ribbon config via versioning.
        if (ribbonConfig.version < defaultConfig.version) {
            ribbonVersionHandler(ribbonConfig, mailboxInfo);
            initializeMailRibbonConfiguration(ribbonConfig, mailboxInfo);
        }
        else {
            // ribbonConfig.version === defaultConfig.version
            // We don't have any extra work we need to do if the ribbon versions are the same, however
            // we still want to validityCheck to safeguard engineers and users in case they end up in a bad Ribbon state.
            // If we fail this validityCheck, default back to defaultConfig.
            if (!ribbonValidityCheck(ribbonConfig.singleline, ribbonConfig.version, mailboxInfo, true /*isSingleLine*/, undefined /* shouldPrune*/)) {
                logUsage("RibbonVersioning_FailedValidityCheck_EqualConfigVersions", mailboxInfo, [
                    ribbonConfig.version,
                    defaultConfig.version,
                    "singleline",
                ]);
            }
            else if (isGlobalFeatureEnabled("mon-ribbon-mlr-customization") &&
                !ribbonValidityCheck(ribbonConfig.multiline, ribbonConfig.version, mailboxInfo, false /*isSingleLine*/, undefined /* shouldPrune*/)) {
                logUsage("RibbonVersioning_FailedValidityCheck_EqualConfigVersions", mailboxInfo, [
                    ribbonConfig.version,
                    defaultConfig.version,
                    "multiline",
                ]);
            }
            else {
                initializeMailRibbonConfiguration(ribbonConfig, mailboxInfo);
            }
        }
    }
}

/**
 * The tabs in this function are currently uncustomizable, and therefore, don't need versioning and
 * will *always* use the default version.
 * Note that when turning on customization for these tabs, be sure to remove the tabs below so that versioning can begin.
 * Additionally, when adding a new tab, be sure to add it to this function.
 */
function updateUncustomizableTabs(ribbonConfig: any) {
    const defaultRibbonConfig = getDefaultConfig();
    if (isGlobalFeatureEnabled("mon-ribbon-mlr-customization")) {
        ribbonConfig.multiline.formatTab = defaultRibbonConfig.multiline.formatTab;
        ribbonConfig.multiline.optionsTab = defaultRibbonConfig.multiline.optionsTab;
        ribbonConfig.multiline.messageTab = defaultRibbonConfig.multiline.messageTab;
        ribbonConfig.multiline.insertTab = defaultRibbonConfig.multiline.insertTab;
        ribbonConfig.multiline.notesTab = defaultRibbonConfig.multiline.notesTab;
        ribbonConfig.multiline.tableFormatTab = defaultRibbonConfig.multiline.tableFormatTab;
        ribbonConfig.multiline.readTab = defaultRibbonConfig.multiline.readTab;
        ribbonConfig.multiline.helpTab = defaultRibbonConfig.multiline.helpTab;
        ribbonConfig.multiline.drawTab = defaultRibbonConfig.multiline.drawTab;
        ribbonConfig.multiline.pictureTab = defaultRibbonConfig.multiline.pictureTab;
        ribbonConfig.multiline.searchToolsTab = defaultRibbonConfig.multiline.searchToolsTab;
        ribbonConfig.multiline.mailMergeTab = defaultRibbonConfig.multiline.mailMergeTab;
    }
    else {
        ribbonConfig.multiline = defaultRibbonConfig.multiline;
    }
    
    ribbonConfig.singleline.formatTab = defaultRibbonConfig.singleline.formatTab;
    ribbonConfig.singleline.optionsTab = defaultRibbonConfig.singleline.optionsTab;
    ribbonConfig.singleline.messageTab = defaultRibbonConfig.singleline.messageTab;
    ribbonConfig.singleline.insertTab = defaultRibbonConfig.singleline.insertTab;
    ribbonConfig.singleline.notesTab = defaultRibbonConfig.singleline.notesTab;
    ribbonConfig.singleline.tableFormatTab = defaultRibbonConfig.singleline.tableFormatTab;
    ribbonConfig.singleline.readTab = defaultRibbonConfig.singleline.readTab;
    ribbonConfig.contextualTabs = defaultRibbonConfig.contextualTabs;
    ribbonConfig.singleline.helpTab = defaultRibbonConfig.singleline.helpTab;
    ribbonConfig.singleline.drawTab = defaultRibbonConfig.singleline.drawTab;
    ribbonConfig.singleline.pictureTab = defaultRibbonConfig.singleline.pictureTab;
    ribbonConfig.singleline.searchToolsTab = defaultRibbonConfig.singleline.searchToolsTab;
    ribbonConfig.singleline.mailMergeTab = defaultRibbonConfig.singleline.mailMergeTab;
}

/**
 * If the flight `mon-ribbon-customization-hard-reset` is turned on, then upon load
 * the ribbon will return to the default non-customizable state.
 * This is just in case versioning causes the ribbon to become in a bad state.
 */
function hardResetRibbon(mailboxInfo: MailboxInfo) {
    const ribbonConfigString: string = JSON.stringify(defaultConfig);
    updateAccountScopeUserSettingsAndService(mailboxInfo, {
        ViewStateConfiguration: {
            MailRibbonConfig: ribbonConfigString,
        },
    });
}

/**
 * Sanity check the user's single line ribbon config to make sure it's not in an egregiously erroneous state.
 * We do this by checking for the MailRibbonGroupId.Group_RibbonCustomizer, the Ribbon group that holds the customizer entry point.
 * This is in response to ADO246765, and is targetted to fix those particular users who are in the bad state.
 */
function checkForSinglelineCustomizationGroup(singlelineRibbonConfig: RibbonConfiguration) {
    return singlelineRibbonConfig.homeTab.layout.some(item => item.groupId === 122);
}

/**
 * This function is being used to initialize the ribbon from consumers that meet the following criteria:
 * - don't have any corp account added on their Monarch
 * - haven't customized their ribbon
 */
function initializeConsumerMailRibbonConfiguration(mailboxInfo: MailboxInfo) {
    const controlsToOverflow: RibbonControlId[] = [
        658,
        509,
        548,
        549,
        559,
        571,
        572,
        663,
    ];
    
    // Update owa-mail-ribbon-store for this tab configuration.
    setTabControlsInOverflow(mailboxInfo, "homeTab", controlsToOverflow);
}
